import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtDate, todayISO } from "@/lib/utils-format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Receipt } from "lucide-react";

const CATEGORIES = [
  "Payroll", "Equipment", "Gas/Fuel", "Materials", "Repairs",
  "Marketing", "Insurance", "Subcontractors", "Supplies", "Other",
];
const METHODS = ["Cash", "Zelle", "Check", "Credit/Debit Card", "ACH Transfer"];

const empty = () => ({
  date: todayISO(),
  category: "Materials",
  description: "",
  amount: 0,
  payment_method: "Cash",
  job_id: "",
  receipt_url: "",
  notes: "",
});

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [catFilter, setCatFilter] = useState("all");

  const load = async () => {
    const [a, b] = await Promise.all([endpoints.list("expenses"), endpoints.list("jobs")]);
    setItems(a.data); setJobs(b.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (catFilter === "all" ? items : items.filter((x) => x.category === catFilter)),
    [items, catFilter]
  );

  const totals = useMemo(() => {
    const byCat = {};
    let grand = 0;
    items.forEach((x) => {
      const a = Number(x.amount || 0);
      byCat[x.category] = (byCat[x.category] || 0) + a;
      grand += a;
    });
    return { byCat, grand };
  }, [items]);

  const startNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const startEdit = (x) => { setEditing(x); setForm({ ...empty(), ...x }); setOpen(true); };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount"); return; }
    const data = { ...form, amount: Number(form.amount) };
    if (editing) await endpoints.update("expenses", editing.id, data);
    else await endpoints.create("expenses", data);
    toast.success("Expense saved");
    setOpen(false); load();
  };
  const remove = async (id) => { if (!window.confirm("Delete this expense?")) return; await endpoints.remove("expenses", id); load(); };

  return (
    <Layout title="Expenses" subtitle={`${items.length} expenses · ${fmtMoney(totals.grand)} total`}>
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48" data-testid="expenses-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Button onClick={startNew} data-testid="expenses-new"><Plus className="size-4 mr-1.5"/>New Expense</Button>
        </div>
      </div>

      <Card className="rounded-xl overflow-hidden mb-6">
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="expenses-table">
          <thead className="bg-muted/40"><tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <th className="p-4 font-semibold">Date</th>
            <th className="p-4 font-semibold">Category</th>
            <th className="p-4 font-semibold">Description</th>
            <th className="p-4 font-semibold">Method</th>
            <th className="p-4 font-semibold">Job</th>
            <th className="p-4 font-semibold">Amount</th>
            <th className="p-4 w-20"></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-muted-foreground">No expenses yet.</td></tr>}
            {filtered.map((x) => {
              const job = jobs.find((j) => j.id === x.job_id);
              return (
                <tr key={x.id} className="border-t border-border hover:bg-muted/30">
                  <td className="p-4">{fmtDate(x.date)}</td>
                  <td className="p-4"><span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{x.category}</span></td>
                  <td className="p-4">{x.description}</td>
                  <td className="p-4 text-xs text-muted-foreground">{x.payment_method}</td>
                  <td className="p-4 text-xs">{job ? `${job.client_name} · ${job.service}` : "—"}</td>
                  <td className="p-4 font-mono font-semibold text-destructive">-{fmtMoney(x.amount)}</td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="size-7" onClick={() => startEdit(x)}><Pencil className="size-3.5"/></Button>
                      <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={() => remove(x.id)}><Trash2 className="size-3.5"/></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </Card>

      {/* Category Totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="expenses-totals">
        {CATEGORIES.map((c) => {
          const v = totals.byCat[c] || 0;
          return (
            <div key={c} className="stat-card">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{c}</div>
              <div className="font-display text-lg font-bold mt-1 font-mono">{fmtMoney(v)}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-4 rounded-xl bg-card border border-border flex items-center justify-between">
        <div className="font-display text-lg font-semibold flex items-center gap-2"><Receipt className="size-4 text-primary"/>Total Expenses (Recorded)</div>
        <div className="font-display text-2xl font-bold font-mono text-destructive">{fmtMoney(totals.grand)}</div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="expense-form">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Expense" : "New Expense"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="e.g. Fuel for Crew B truck"/></div>
            <div className="space-y-1.5"><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} data-testid="expense-amount"/></div>
            <div className="space-y-1.5"><Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v)=>setForm({...form, payment_method:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Related Job (optional)</Label>
              <Select value={form.job_id || "none"} onValueChange={(v)=>setForm({...form, job_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.client_name} · {j.service} · {fmtDate(j.date)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Receipt URL (placeholder)</Label><Input value={form.receipt_url || ""} onChange={(e)=>setForm({...form, receipt_url:e.target.value})} placeholder="Paste a link or filename for now"/></div>
            <div className="space-y-1.5 col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="expense-save">Save Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
